import React, { useState, useRef } from 'react';
import MaterialTable, { MTableToolbar } from 'material-table';
import { Button } from '@material-ui/core';
import { FieldArray, Form, Formik } from 'formik';
import FileUploadModal from '../../components/files/FileUploadModal';
import { useLazyQuery, useMutation } from '@apollo/client';
import { FILE_QUERY, UPLOAD_FILE } from '../../components/files/fileQueries';
import {CREATE_LANDBOOK, DELETE_LANDBOOK, EDIT_LANDBOOK, GET_LANDBOOKS} from "../../components/tracts/TractQueries";
import { useSnackbar } from 'notistack';

const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
};

const LandBooks = ({ Tract, match }) => {
    const tractID = match?.params?.tractID || Tract.id;
    const [addLandBook] = useMutation(CREATE_LANDBOOK);
    const [editLandBook] = useMutation(EDIT_LANDBOOK);
    const [DeleteLandBook] = useMutation(DELETE_LANDBOOK);
    const [uploadFile] = useMutation(UPLOAD_FILE);
    const [highlightedRowID, setHighlightedRow] = useState(null);
    const [fileUploadOpen, setFileUploadOpen] = useState(false);
    const [fileKey, setFileKey] = useState('');
    const [rowInfo, setRowInfo] = useState(null);
    const [viewUrl, setViewUrl] = useState(null);
    const [viewFileName, setViewFileName] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const addFileInputRef = useRef(null);
    const { enqueueSnackbar } = useSnackbar();

    const [loadFile] = useLazyQuery(FILE_QUERY, { variables: { fileName: fileKey } });

    const formikData = {
        landBook: Tract.landBook || []
    };

    return (
        <Formik initialValues={formikData} enableReinitialize onSubmit={() => {}}>
            {(props) => (
                <Form>
                    <FieldArray name="landBook">
                        {({ push, remove, insert }) => {
                            const columns = [
                                { title: 'File Name', field: 'fileName', editable: 'never', render: rowData => (rowData?.fileName) || 'Upload file after adding row.' },
                                { title: 'Date Updated', field: 'dateUpdated', type: 'date', editable: 'never',render: rowData => rowData?.dateUpdated ? formatDateForDisplay(rowData.dateUpdated) : 'No Date', },
                                { title: 'Date Created', field: 'dateFiled', type: 'date', editable: 'never', render: rowData => rowData?.dateFiled ? formatDateForDisplay(rowData.dateFiled) : 'No Date', },
                                {
                                    title: 'Notes',
                                    field: 'notes',
                                    cellStyle: { minWidth: 300, whiteSpace: 'nowrap' },
                                },
                            ];

                            return (
                                <>
                                    <MaterialTable
                                        columns={columns}
                                        data={props.values.landBook.map((lb) => ({ ...lb }))}
                                        components={{
                                            Toolbar: (toolbarProps) => (
                                                <div
                                                    style={{
                                                        marginTop: -29,
                                                        backgroundColor: '#1976d2',
                                                        height: 61,
                                                        display: 'flex',
                                                        justifyContent: 'left',
                                                        alignItems: 'center',

                                                    }}
                                                >
                                                    <MTableToolbar {...toolbarProps} />
                                                    <Button
                                                        variant='contained'
                                                        color='primary'
                                                        size='small'
                                                        disabled={isAdding}
                                                        onClick={() => addFileInputRef.current?.click()}
                                                        style={{ fontSize: '0.75rem', minWidth: 'auto', padding: '4px 10px' }}
                                                    >
                                                        {isAdding ? 'Adding…' : 'Add'}
                                                    </Button>
                                                </div>
                                            ),
                                        }}
                                        editable={{
                                            onRowUpdate: async (newData, oldData) => {
                                                newData.dateUpdated = new Date().toISOString();

                                                try {
                                                    const { data } = await editLandBook({
                                                        variables: {
                                                            id: newData.id,             // REQUIRED ID
                                                            fileName: newData.fileName,
                                                            notes: newData.notes,
                                                            dateFiled: newData.dateFiled,
                                                            dateUpdated: newData.dateUpdated,
                                                        }
                                                    });

                                                    const updatedRow = data.UpdateLandBook;
                                                    remove(oldData.tableData.id);
                                                    insert(oldData.tableData.id, updatedRow);
                                                } catch (err) {
                                                    console.error(err);
                                                }
                                            },

                                            onRowDelete: async (oldData) => {
                                                return DeleteLandBook({ variables: { id: oldData.id } }).then(() => {
                                                    remove(oldData.tableData.id);
                                                    const deletedFileName = oldData.fileName ? oldData.fileName : `${tractID}/${oldData.id}`;
                                                    if (viewFileName === deletedFileName) {
                                                        setViewUrl(null);
                                                        setViewFileName('');
                                                    }
                                                });
                                            },
                                        }}

                                        actions={[
                                            {
                                                icon: 'addFile',
                                                tooltip: 'Upload File',
                                                onClick: (event, rowData) => {
                                                    setHighlightedRow(rowData.tableData.id);
                                                    setFileKey(`${tractID}/${rowData.id}`);
                                                    setRowInfo(rowData);
                                                    setFileUploadOpen(true);
                                                },
                                            },
                                            {
                                                icon: 'map',
                                                tooltip: 'View File',
                                                onClick: async (event, rowData) => {
                                                    const { id } = rowData;
                                                    const fileName = rowData.fileName ? rowData.fileName : `${tractID}/${id}`;
                                                    setFileKey(fileName);
                                                    if (viewUrl && viewFileName === fileName) {
                                                        setViewUrl(null);
                                                        setViewFileName('');
                                                        return;
                                                    }
                                                    try {
                                                        const response = await loadFile({ variables: { fileName } });
                                                        const { data: { getFile } } = response;
                                                        setViewUrl(getFile[0].requestUrl);
                                                        setViewFileName(fileName);
                                                    } catch (e) {
                                                        console.log(e);
                                                    }
                                                }
                                            }
                                        ]
                                        }
                                        onRowClick={(event, rowData) => setHighlightedRow(rowData.tableData?.id)}
                                        options={{
                                            maxBodyHeight: 'calc(100vh - 150px)',
                                            searchFieldStyle: {
                                                backgroundColor: '#fff',
                                                color: '#000',
                                                border: 'none',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                            },
                                            headerStyle: {
                                                backgroundColor: '#1976d2',
                                                color: '#FFF',
                                                textAlign: 'left',
                                                whiteSpace: 'pre',
                                            },
                                            rowStyle: (rowData) => ({
                                                backgroundColor:
                                                    rowData.tableData?.id === highlightedRowID ? '#c5c5c5' : 'white',
                                                transition: 'background-color 0.3s ease',
                                            }),
                                            addRowPosition: 'first',
                                            searchFieldAlignment: 'left',
                                            toolbarButtonAlignment: 'left',
                                            paging: false,
                                            showTitle: false,
                                        }}
                                    />

                                    <input
                                        id="landbookFileInput"
                                        ref={addFileInputRef}
                                        type="file"
                                        accept="image/*, .pdf"
                                        style={{ display: 'none' }}
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;
                                            e.target.value = '';
                                            setIsAdding(true);
                                            const now = new Date().toISOString();
                                            try {
                                                const { data: addData } = await addLandBook({
                                                    variables: {
                                                        tractID: Tract.id,
                                                        fileName: 'uploading...',
                                                        notes: '',
                                                        dateFiled: now,
                                                        dateUpdated: now,
                                                    },
                                                });
                                                const createdRow = addData?.createLandBook;
                                                const fileExtension = file.name.split('.').pop();
                                                const uploadName = `${tractID}/${createdRow.id}.${fileExtension}`;
                                                await uploadFile({
                                                    variables: { file, fileSize: `${file.size}`, name: uploadName },
                                                });
                                                const { data: editData } = await editLandBook({
                                                    variables: {
                                                        id: createdRow.id,
                                                        fileName: uploadName,
                                                        notes: '',
                                                        dateFiled: now,
                                                        dateUpdated: now,
                                                    },
                                                });
                                                push(editData.UpdateLandBook);
                                                enqueueSnackbar('Land Book added successfully', { variant: 'success' });
                                            } catch (err) {
                                                console.error(err);
                                                enqueueSnackbar('Failed to add Land Book', { variant: 'error' });
                                            } finally {
                                                setIsAdding(false);
                                            }
                                        }}
                                    />

                                    {viewUrl && (
                                        <div style={{ marginTop: 16, overflow: 'hidden' }}>
                                            <iframe
                                                src={viewUrl}
                                                title="Land Book Document"
                                                style={{ width: '100%', height: '80vh', border: 'none', display: 'block', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                                            />
                                        </div>
                                    )}

                                    <FileUploadModal
                                        open={fileUploadOpen}
                                        setFileUploadOpen={setFileUploadOpen}
                                        fileKey={fileKey}
                                        rowInfo={rowInfo}
                                        values={props.values}
                                        setFieldValue={props.setFieldValue}
                                        recordUpdate={(variables) =>
                                            editLandBook({
                                                variables,
                                                refetchQueries: [
                                                    { query: GET_LANDBOOKS, variables: { tractID } }
                                                ],
                                            })
                                        }
                                        uploadType={"LANDBOOK"}
                                    />
                                </>
                            );
                        }}
                    </FieldArray>
                </Form>
            )}
        </Formik>
    );
};

export default LandBooks;